import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Wand2, Loader2, RotateCw, ArrowRight, PenLine, X, CalendarClock } from 'lucide-react';
import { blogsApi } from '../api/blogs';
import { useToast } from '../hooks/use-toast';

/**
 * AI Blog Studio — sits above the drafts list on /admin.
 * • Suggest 10 topics (mode: pillars / trending / both)
 * • Write a blog on any topic (yours or a suggested one)
 * • Length toggle: In-depth SEO / Adaptive
 * • AI cover image (Gemini Nano Banana) toggle
 * • Each generated blog lands as a DRAFT — visible below with 1-click publish
 */
const modeLabel = { pillars: 'Pillars', trending: 'Trending', both: 'Both' };
const lengthLabel = { seo: 'In-depth SEO', adaptive: 'Adaptive' };

const AIStudio = ({ onDraftCreated }) => {
  const [mode, setMode] = useState('both');
  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [customTopic, setCustomTopic] = useState('');
  const [length, setLength] = useState('seo');
  const [includeImage, setIncludeImage] = useState(true);
  const [writingTopic, setWritingTopic] = useState(null); // string being written
  const [dailyTopics, setDailyTopics] = useState([]);
  const [regeneratingDaily, setRegeneratingDaily] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { topics: daily } = await blogsApi.dailyTopics();
        setDailyTopics(daily || []);
      } catch (err) {
        // Best-effort — the daily topics panel just stays hidden if this fails.
        console.warn('Daily topics fetch failed:', err?.response?.data?.detail || err?.message);
      }
    })();
  }, []);

  const regenerateDaily = async () => {
    setRegeneratingDaily(true);
    try {
      await blogsApi.regenerateDailyTopics();
      const { topics: daily } = await blogsApi.dailyTopics();
      setDailyTopics(daily || []);
      toast({ title: 'Fresh topics generated' });
    } catch (err) {
      toast({
        title: 'Regenerate failed',
        description: err?.response?.data?.detail || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingDaily(false);
    }
  };

  const dismissDaily = async (id) => {
    setDailyTopics((ts) => ts.filter((t) => t.id !== id));
    try {
      await blogsApi.dismissDailyTopic(id);
    } catch (err) {
      // Silent — the UI already removed it. Log for debugging.
      console.warn('Dismiss daily topic failed:', err?.response?.data?.detail || err?.message);
    }
  };

  const suggest = async () => {
    setLoadingTopics(true);
    try {
      const { topics: ideas } = await blogsApi.aiTopics(mode, 10);
      setTopics(ideas || []);
      if (!ideas?.length) toast({ title: 'No topics returned', variant: 'destructive' });
    } catch (err) {
      toast({
        title: 'Topic generation failed',
        description: err?.response?.data?.detail || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setLoadingTopics(false);
    }
  };

  const write = async (topic) => {
    const clean = (topic || '').trim();
    if (clean.length < 5) {
      toast({ title: 'Topic too short', description: 'Give the AI at least 5 characters.', variant: 'destructive' });
      return;
    }
    setWritingTopic(clean);
    try {
      const blog = await blogsApi.aiGenerate({
        topic: clean,
        length,
        include_image: includeImage,
      });
      toast({
        title: 'Draft ready',
        description: includeImage
          ? 'Cover image is generating in the background.'
          : 'Open it to review + publish.',
      });
      onDraftCreated?.(blog);
      // remove the used topic from the suggestions list
      setTopics((ts) => ts.filter((t) => t.title !== clean));
      setDailyTopics((ts) => ts.filter((t) => t.title !== clean));
      setCustomTopic('');
    } catch (err) {
      toast({
        title: 'Blog generation failed',
        description: err?.response?.data?.detail || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setWritingTopic(null);
    }
  };

  return (
    <section
      data-testid="ai-studio"
      className="rounded-2xl border border-[#F39200]/30 bg-gradient-to-br from-[#F39200]/8 via-[#F39200]/4 to-transparent p-5 md:p-6 mb-8"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="text-[#F39200]" size={18} />
        <h2 className="text-lg md:text-xl font-extrabold tracking-tight text-neutral-900">AI Blog Studio</h2>
        <span className="ml-2 text-[10px] uppercase tracking-widest font-semibold text-[#F39200] bg-[#F39200]/10 border border-[#F39200]/30 rounded-full px-2 py-0.5">
          Beta
        </span>
      </div>
      <p className="text-sm text-neutral-600 mb-4">
        Get 10 SEO topic ideas or write a full 1,200-1,500 word draft with a brand-styled cover — all as drafts you review before publishing.
      </p>

      {/* Fresh daily topics — auto-generated every morning at 09:00 IST */}
      {dailyTopics.length > 0 && (
        <div className="mb-5 bg-white rounded-xl border border-[#F39200]/25 p-4" data-testid="daily-topics">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#F39200]">
              <CalendarClock size={12} /> Fresh from this morning ({dailyTopics.length})
            </div>
            <button
              onClick={regenerateDaily}
              disabled={regeneratingDaily}
              data-testid="regenerate-daily-btn"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-600 hover:text-[#F39200] disabled:opacity-60"
            >
              {regeneratingDaily ? <Loader2 size={11} className="animate-spin" /> : <RotateCw size={11} />}
              {regeneratingDaily ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <div className="space-y-1.5">
            {dailyTopics.map((t) => (
              <div
                key={t.id}
                data-testid={`daily-topic-${t.id}`}
                className="group grid grid-cols-[1fr_auto_auto] gap-2 items-center px-3 py-2 rounded-lg hover:bg-[#F39200]/5 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 truncate">{t.title}</div>
                  {t.angle && (
                    <div className="text-xs text-neutral-500 truncate">
                      {t.angle}
                      {t.primary_keyword && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-[#F39200]">
                          {t.primary_keyword}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => write(t.title)}
                  disabled={!!writingTopic}
                  data-testid={`daily-write-${t.id}`}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[11px] font-semibold text-white bg-[#F39200] hover:bg-neutral-900 transition-colors disabled:opacity-60"
                >
                  {writingTopic === t.title ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                  Write
                </button>
                <button
                  onClick={() => dismissDaily(t.id)}
                  data-testid={`daily-dismiss-${t.id}`}
                  title="Dismiss"
                  className="w-7 h-8 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls row */}
      <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end mb-4">
        {/* Topic source pills */}
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">Topic source</div>
          <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-1" data-testid="topic-mode-toggle">
            {['pillars', 'trending', 'both'].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                data-testid={`topic-mode-${m}`}
                className={`px-3 h-8 text-xs font-semibold rounded-md transition-colors ${
                  mode === m ? 'bg-[#F39200] text-white' : 'text-neutral-700 hover:text-neutral-900'
                }`}
              >
                {modeLabel[m]}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={suggest}
          disabled={loadingTopics}
          data-testid="suggest-topics-btn"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {loadingTopics ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
          {loadingTopics ? 'Thinking…' : 'Suggest 10 topics'}
        </button>
      </div>

      {/* Length + image toggles */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">Length</div>
        <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-1" data-testid="length-toggle">
          {['seo', 'adaptive'].map((l) => (
            <button
              key={l}
              onClick={() => setLength(l)}
              data-testid={`length-${l}`}
              className={`px-3 h-8 text-xs font-semibold rounded-md transition-colors ${
                length === l ? 'bg-neutral-900 text-white' : 'text-neutral-700 hover:text-neutral-900'
              }`}
            >
              {lengthLabel[l]}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeImage}
            onChange={(e) => setIncludeImage(e.target.checked)}
            data-testid="include-image-toggle"
            className="w-4 h-4 rounded border-neutral-300 text-[#F39200] focus:ring-[#F39200]"
          />
          AI cover image (Nano Banana)
        </label>
      </div>

      {/* Custom topic writer */}
      <div className="grid md:grid-cols-[1fr_auto] gap-2 mb-5">
        <input
          value={customTopic}
          onChange={(e) => setCustomTopic(e.target.value)}
          placeholder="Or type a topic and let AI write it: e.g. GST refund process for exporters"
          data-testid="custom-topic-input"
          className="w-full h-11 px-3 rounded-lg border border-neutral-200 bg-white focus:border-[#F39200] outline-none text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !writingTopic) write(customTopic);
          }}
        />
        <button
          onClick={() => write(customTopic)}
          disabled={!!writingTopic || customTopic.trim().length < 5}
          data-testid="write-custom-btn"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-[#F39200] hover:bg-neutral-900 text-white text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {writingTopic === customTopic.trim() ? <Loader2 size={14} className="animate-spin" /> : <PenLine size={14} />}
          Write blog
        </button>
      </div>

      {/* Suggested topics */}
      {topics.length > 0 && (
        <div className="space-y-2" data-testid="topic-list">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 mb-1">
            {topics.length} suggested topics
          </div>
          {topics.map((t, i) => (
            <div
              key={`${t.title}-${i}`}
              data-testid={`topic-item-${i}`}
              className="group grid grid-cols-[1fr_auto] gap-3 items-center bg-white border border-black/5 rounded-xl px-4 py-3 hover:border-[#F39200]/40 hover:shadow-sm transition-all"
            >
              <div className="min-w-0">
                <div className="font-semibold text-sm text-neutral-900 truncate">{t.title}</div>
                {t.angle && (
                  <div className="text-xs text-neutral-500 mt-0.5 truncate">
                    {t.angle}
                    {t.primary_keyword && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-[#F39200] bg-[#F39200]/10 rounded px-1.5 py-0.5">
                        {t.primary_keyword}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => write(t.title)}
                disabled={!!writingTopic}
                data-testid={`write-topic-${i}`}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold text-neutral-900 border border-neutral-300 hover:border-[#F39200] hover:text-[#F39200] transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {writingTopic === t.title ? (
                  <>
                    <Loader2 size={12} className="animate-spin" /> Writing…
                  </>
                ) : (
                  <>
                    <Wand2 size={12} /> Write
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {writingTopic && (
        <div className="mt-4 flex items-center gap-2 text-xs text-neutral-600" data-testid="writing-indicator">
          <Loader2 size={12} className="animate-spin text-[#F39200]" />
          Writing "{writingTopic.slice(0, 60)}{writingTopic.length > 60 ? '…' : ''}" — this takes ~60s.
        </div>
      )}
    </section>
  );
};

export default AIStudio;
